import React, { useState, useEffect } from "react";
import { NavLink, useParams } from "react-router-dom";
import { Page, Document, PDFViewer } from "@react-pdf/renderer";
import { DEFAULT_COMPANY, formatDocDate, enhanceLineItemsWithTax } from "../../../../../utils/pdfDocumentUtils";
import {
  arPdfStyles,
  ArDocumentTitle,
  ArDocumentHeader,
  ArBuyerSiteSection,
  ArServicesTable,
  ArAmountInWords,
  ArHsnSummaryTable,
  ArBankSignatureFooter,
  ArPageNumber,
  ArRemarksSection,
} from "../../../../../components/pdf/ArDocumentPdfLayout";

const CreditNoteDocument = ({ allData, company, customer, site, project }) => {
  const creditNote = allData?.credit_note || {};
  const lineItems = enhanceLineItemsWithTax(allData?.line_items || [], creditNote);
  const totalAmount = parseFloat(creditNote.total_amount || 0);

  const detailRows = [
    { label: "Credit Note No.", value: creditNote.doc_no },
    { label: "Dated", value: formatDocDate(creditNote.credit_note_date) },
    {
      label: "Reference No. & Date",
      value: creditNote.original_invoice_no
        ? `${creditNote.original_invoice_no}${creditNote.original_invoice_date ? ` dated ${formatDocDate(creditNote.original_invoice_date)}` : ""}`
        : null,
    },
    { label: "Buyer's Order No.", value: creditNote.buyer_order_no },
    { label: "Dated", value: creditNote.buyer_order_date ? formatDocDate(creditNote.buyer_order_date) : null },
    { label: "Destination", value: creditNote.destination || site?.site_name || project?.project_name },
  ].filter((row) => row.value);

  return (
    <Document>
      <Page size="A4" style={arPdfStyles.page}>
        <ArDocumentTitle
          title="CREDIT NOTE"
          subtitle="(As per Rule 46 of CGST Rules, 2017 & Section 31 of CGST Act, 2017)"
        />
        <ArDocumentHeader company={company} detailRows={detailRows} />
        <ArBuyerSiteSection
          customer={customer}
          site={site}
          project={project}
          placeOfSupply={creditNote.place_of_supply}
        />
        <ArRemarksSection remarks={creditNote.reason ? `Reason: ${creditNote.reason}` : null} />
        <ArServicesTable lineItems={lineItems} doc={creditNote} />
        <ArAmountInWords amount={totalAmount} wordsOverride={creditNote.amount_in_words} />
        <ArHsnSummaryTable enhancedItems={lineItems} doc={creditNote} />
        <ArBankSignatureFooter company={company} docTypeLabel="Credit Note" />
        <ArPageNumber />
      </Page>
    </Document>
  );
};

export default function CreditNotePrint() {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [allData, setAllData] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [site, setSite] = useState(null);
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessError, setAccessError] = useState(null);

  useEffect(() => {
    const fetchCreditNoteData = async () => {
      try {
        const token = localStorage.getItem("zTaskAuthToken");
        const API_BASE = process.env.REACT_APP_NODE_SERVICE_BASE_URL || "http://localhost:8081";

        const creditNoteResponse = await fetch(`${API_BASE}/credit-note/api/credit-notes/${id}/pdf`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (creditNoteResponse.status === 403) {
          const err = await creditNoteResponse.json();
          setAccessError(err.message || "PDF is available only after the credit note is approved.");
          return;
        }

        if (!creditNoteResponse.ok) {
          throw new Error("Failed to fetch credit note");
        }

        const pdfData = await creditNoteResponse.json();
        setAllData(pdfData);
        setCustomer(pdfData.customer);
        setSite(pdfData.site);
        setProject(pdfData.project);
        setCompany(DEFAULT_COMPANY);
      } catch (error) {
        console.error("Error fetching credit note data:", error);
        alert(`Error loading credit note: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchCreditNoteData();
    }
  }, [id]);

  if (isLoading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p>Loading credit note data...</p>
      </div>
    );
  }

  if (accessError) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p style={{ color: "#c62828" }}>{accessError}</p>
        <NavLink to="/ar/credit-notes">
          <button style={{ padding: "8px 16px", background: "#007bff", color: "white", border: "none", borderRadius: "4px" }}>
            Back to Credit Notes
          </button>
        </NavLink>
      </div>
    );
  }

  if (!allData) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p>Credit note not found.</p>
        <NavLink to="/ar/credit-notes">
          <button style={{ padding: "8px 16px", background: "#007bff", color: "white", border: "none", borderRadius: "4px" }}>
            Back to Credit Notes
          </button>
        </NavLink>
      </div>
    );
  }

  return (
    <>
      <NavLink to="/ar/credit-notes">
        <button style={{ marginBottom: "10px", padding: "8px 16px", background: "#6c757d", color: "white", border: "none", borderRadius: "4px" }}>
          Back To Credit Notes
        </button>
      </NavLink>
      <PDFViewer style={arPdfStyles.viewer}>
        <CreditNoteDocument allData={allData} company={company} customer={customer} site={site} project={project} />
      </PDFViewer>
    </>
  );
}
